import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { draughtsAPI, platformAPI } from '../services/api';
import { buildInviteLink } from '../utils/inviteLinks';
import '../styles/Dashboard.css';

const FRIENDLY_TURN_OPTIONS = [
  { seconds: 10, label: '10 sec', icon: '⚡', description: 'Fast-paced' },
  { seconds: 15, label: '15 sec', icon: '🔥', description: 'Quick thinking' },
  { seconds: 20, label: '20 sec', icon: '🎯', description: 'Balanced' },
  { seconds: 30, label: '30 sec', icon: '♟️', description: 'Strategic' },
];

const BETTING_TURN_OPTIONS = [
  { seconds: 10, label: '10 sec', icon: '⚡', description: 'Blitz' },
  { seconds: 15, label: '15 sec', icon: '🔥', description: 'Rapid' },
  { seconds: 20, label: '20 sec', icon: '🎯', description: 'Classic' },
];

const FRIENDLY_TURN_OPTIONS_EXTENDED = [
  { seconds: 120, label: '2 min', icon: '⏱️', description: '2 minutes' },
  ...FRIENDLY_TURN_OPTIONS,
];

const BETTING_TURN_OPTIONS_EXTENDED = [
  { seconds: 120, label: '2 min', icon: '⏱️', description: '2 minutes' },
  ...BETTING_TURN_OPTIONS,
  { seconds: 30, label: '30 sec', icon: '♟️', description: 'Strategic' },
];

const CREATE_KIND_BOT = 'bot';
const CREATE_KIND_FRIENDLY = 'friendly';
const CREATE_KIND_REAL = 'real';
const QUICK_BOT_NAMES_BY_LEVEL = {
  1: ['LagosRookie', 'NaijaStarter', 'DraftCub'],
  2: ['KingBridge', 'DeltaTact', 'SavannaMind'],
  3: ['ObaMaster', 'EagleCrown', 'GrandKano'],
};

export default function DraughtsLobby() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const [mode, setMode] = useState('friendly');
  const [openMatches, setOpenMatches] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [ongoingMatches, setOngoingMatches] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});

  const [wager, setWager] = useState(50);
  const [minimumWager, setMinimumWager] = useState(50);
  const [opponentId, setOpponentId] = useState('');
  const [botLevel, setBotLevel] = useState(1);
  const [botWager, setBotWager] = useState(0);
  const [turnTimeoutSeconds, setTurnTimeoutSeconds] = useState(15);
  const [joinId, setJoinId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKind, setCreateKind] = useState(CREATE_KIND_FRIENDLY);
  const [roomSearch, setRoomSearch] = useState('');
  const [activeTab, setActiveTab] = useState('my');
  const [quickSearching, setQuickSearching] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const userId = Number(user?.id || 0);

  useEffect(() => {
    const nextMode = String(params.get('mode') || '').toLowerCase() === 'betting' ? 'betting' : 'friendly';
    setMode(nextMode);
  }, [params]);

  const options = useMemo(() => {
    let opts = mode === 'betting' ? BETTING_TURN_OPTIONS_EXTENDED : FRIENDLY_TURN_OPTIONS_EXTENDED;
    if (createKind === CREATE_KIND_BOT) {
      opts = opts.filter(o => o.seconds <= 15);
    }
    return opts;
  }, [mode, createKind]);

  useEffect(() => {
    const current = Number(turnTimeoutSeconds);
    if (!options.some((o) => o.seconds === current)) {
      setTurnTimeoutSeconds(options[0]?.seconds || 15);
    }
    setError('');
  }, [options, turnTimeoutSeconds]);

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setWager((prev) => Math.max(nextMin, Number(prev) || 0));
        }
      } catch {
        // ignore settings failure
      }
    };
    void loadPlatformSettings();
  }, []);

 

  const allMatches = useMemo(() => {
    const combinedRaw = [...ongoingMatches, ...challenges, ...openMatches];
    const map = new Map();
    combinedRaw.forEach((m) => {
      const key = String(m?.id || '');
      if (!key) return;
      if (!map.has(key)) map.set(key, m);
    });
    return Array.from(map.values()).sort((a, b) => {
      const ta = Date.parse(a?.started_at || a?.created_at || '') || Number(a?.id || 0);
      const tb = Date.parse(b?.started_at || b?.created_at || '') || Number(b?.id || 0);
      return tb - ta;
    });
  }, [ongoingMatches, challenges, openMatches]);

  const myCreatedMatches = useMemo(() => allMatches.filter((m) => Number(m.creator_id) === userId), [allMatches, userId]);
  const myJoinedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.opponent_id) === userId && Number(m.creator_id) !== userId),
    [allMatches, userId]
  );
  const availableMatches = useMemo(
    () => allMatches.filter((m) => Number(m.creator_id) !== userId && !m.opponent_id),
    [allMatches, userId]
  );

  const filteredAvailableMatches = useMemo(() => {
    const query = String(roomSearch || '').trim().toLowerCase();
    if (!query) return availableMatches;
    return availableMatches.filter((m) => {
      const id = String(m?.id || '').toLowerCase();
      const creator = String(m?.creator_username || '').toLowerCase();
      return id.includes(query) || creator.includes(query);
    });
  }, [availableMatches, roomSearch]);

  const matchByJoinCode = useMemo(
    () => allMatches.find((m) => String(m.id) === String(joinId || '').trim()),
    [allMatches, joinId]
  );

  const canCreate = useMemo(() => {
    const timeoutValid = options.some((o) => o.seconds === Number(turnTimeoutSeconds));
    if (!timeoutValid) return false;
    if (mode === 'friendly') return true;
    const w = Number(wager);
    return Number.isFinite(w)
      && w >= minimumWager
      && Number(user?.balance || 0) >= w;
  }, [mode, options, turnTimeoutSeconds, wager, user?.balance, minimumWager]);

  const loadOpen = async () => {
    try {
      const [friendlyRes, bettingRes] = await Promise.allSettled([
        draughtsAPI.listOpenMatches('friendly'),
        draughtsAPI.listOpenMatches('betting'),
      ]);
      const combined = [];
      if (friendlyRes.status === 'fulfilled') combined.push(...(friendlyRes.value.data || []));
      if (bettingRes.status === 'fulfilled') combined.push(...(bettingRes.value.data || []));
      setOpenMatches(combined);
    } catch { /* non-fatal */ }
  };

  const loadChallenges = async () => {
    try {
      const res = await draughtsAPI.listChallenges();
      setChallenges(res.data || []);
    } catch { /* non-fatal */ }
  };

  const loadOngoing = async () => {
    try {
      const res = await draughtsAPI.listOngoingMatchesAll();
      setOngoingMatches(res.data || []);
    } catch { /* non-fatal */ }
  };

  const loadFriends = async () => {
    try {
      const res = await api.get('/friends');
      setFriends(res.data || []);
    } catch { /* non-fatal */ }
  };

  const loadAll = async () => {
    try {
      setError('');
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing(), loadFriends()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    const id = setInterval(() => {
      void loadOpen();
      void loadChallenges();
      void loadOngoing();
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuickSearch = async () => {
    if (!userId) return;
    try {
      setQuickSearching(true);
      setError('');

      const openRes = await draughtsAPI.listOpenMatches('friendly');
      const openMatchesList = Array.isArray(openRes.data) ? openRes.data : [];
      const candidate = openMatchesList.find((m) => Number(m.creator_id) !== userId);

      if (candidate) {
        await draughtsAPI.joinMatch(candidate.id);
        navigate(`/draughts/duel/${candidate.id}`);
        return;
      }

      const ownOpen = openMatchesList.find((m) => Number(m.creator_id) === userId);
      const created = ownOpen || (await draughtsAPI.createMatch({ mode: 'friendly', wager: 0, turnTimeoutSeconds })).data;
      const matchId = created.id;

      const waitMs = 7000;
      const pollMs = 1000;
      const startAt = Date.now();
      let opponentJoined = false;

      while (Date.now() - startAt < waitMs) {
        await new Promise(resolve => setTimeout(resolve, pollMs));
        const matchRes = await draughtsAPI.getMatch(matchId);
        if (matchRes.data?.opponent_id) { opponentJoined = true; break; }
      }

      if (opponentJoined) { navigate(`/draughts/duel/${matchId}`); return; }

      try { await draughtsAPI.cancelMatch(matchId); } catch { /* ignore */ }
      const level = Math.max(1, Math.min(3, Number(botLevel || 1)));
      const pool = QUICK_BOT_NAMES_BY_LEVEL[level] || QUICK_BOT_NAMES_BY_LEVEL[1];
      const botName = pool[Math.floor(Math.random() * pool.length)] || 'LagosRookie';
      navigate(`/draughts/practice?level=${level}&botName=${encodeURIComponent(botName)}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Quick search failed');
    } finally {
      setQuickSearching(false);
    }
  };

  const getMatchFlags = (m) => {
    const meId = Number(user?.id || 0);
    const isCreator = Number(m.creator_id) === meId;
    const isOpponent = Number(m.opponent_id) === meId;
    const isParticipant = isCreator || isOpponent;
    const isOpen = !m.opponent_id;
    const isPending = m.status === 'pending';
    const isOngoing = m.status === 'in_progress';
    const isAccepted = Boolean(m.accepted_at);
    const isBetting = Number(m.wager || 0) > 0 || String(m.mode || '').toLowerCase() === 'betting';
    const needsAccept = isOpponent && !isAccepted && isPending;
    const canCancel = isCreator && isPending;
    const canContinue = isParticipant && isOngoing;
    const canOpen = isParticipant && isPending;
    const canJoin = isPending && ((isOpen && !isCreator && !isAccepted) || needsAccept);
    const canWatch = (isAccepted || isOngoing) && !isParticipant;
    return { isBetting, isOngoing, canCancel, canContinue, canOpen, canJoin, canWatch, needsAccept };
  };

  const cancelMatch = async (matchId) => {
    try {
      setError('');
      await draughtsAPI.cancelMatch(matchId);
      void loadAll();
      refreshUser?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to cancel');
    }
  };

  const joinMatch = async (m, needsAccept) => {
    try {
      setError('');
      if (needsAccept) await draughtsAPI.acceptMatch(m.id);
      else await draughtsAPI.joinMatch(m.id);
      void loadAll();
      refreshUser?.();
      navigate(`/draughts/duel/${m.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to join');
    }
  };

  const shareMatchLink = async (matchId, modeLabel = 'challenge') => {
    if (!matchId) return;
    const link = buildInviteLink(`/draughts/duel/${matchId}`);
    const message = `Join my Draughts ${modeLabel}: ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Draughts Invite', text: message, url: link });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        window.prompt('Share this Draughts invite link:', link);
      }
    } catch {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          window.prompt('Share this Draughts invite link:', link);
        }
      } catch {
        window.prompt('Share this Draughts invite link:', link);
      }
    }
  };

  const handleCreate = async (overrideMode = null, opts = {}) => {
    try {
      setError('');
      const opp = String(opponentId || '').trim();
      const safeOpponentId = opp ? Number(opp) : null;
      const selectedMode = overrideMode || mode;
      const w = selectedMode === 'betting' ? Math.max(0, Number(wager || 0)) : 0;
      const t = Number(turnTimeoutSeconds);
      if (selectedMode === 'betting' && w < minimumWager) {
        setError(`Wager must be at least ${minimumWager} wikipenny`);
        return false;
      }

      const res = await draughtsAPI.createMatch({
        mode: selectedMode,
        wager: w,
        turnTimeoutSeconds: t,
        opponentId: safeOpponentId,
      });
      const shouldShare = Boolean(opts.shareAfterCreate);
      if (shouldShare) {
        void shareMatchLink(res.data.id, selectedMode === 'betting' ? 'bet room' : 'friendly room');
      }
      refreshUser?.();
      navigate(`/draughts/duel/${res.data.id}`);
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create match');
      return false;
    }
  };

  const openCreateModal = (kind) => {
    setError('');
    setCreateKind(kind);
    setMode(kind === CREATE_KIND_REAL ? 'betting' : 'friendly');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const handleCreateFromModal = async () => {
    if (createKind === CREATE_KIND_BOT) {
      if (Number(botWager) > 0) {
        if (Number(botWager) < minimumWager) {
          setError(`Wager must be at least ${minimumWager} wikipenny`);
          return;
        }
        if (Number(user?.balance || 0) < Number(botWager)) {
          setError('Insufficient balance');
          return;
        }
        const aiIds = [176, 177, 178, 179, 180];
        const randomAi = aiIds[Math.floor(Math.random() * aiIds.length)];
        setError('');
        try {
          const res = await api.post('/draughts/matches', {
            mode: 'betting',
            wager: Number(botWager),
            turnTimeoutSeconds,
            opponentId: randomAi,
          });
          if (res.data?.id) {
            navigate(`/draughts/duel/${res.data.id}`);
            closeCreateModal();
          }
        } catch (e) {
          setError(e.response?.data?.error || 'Failed to create bot challenge');
        }
        return;
      }
      navigate(`/draughts/practice?level=${botLevel}`);
      closeCreateModal();
      return;
    }
    const targetMode = createKind === CREATE_KIND_REAL ? 'betting' : 'friendly';
    const created = await handleCreate(targetMode);
    if (created) closeCreateModal();
  };

  const onJoinByCode = async () => {
    const code = String(joinId || '').trim();
    if (!code) return;

    const match = allMatches.find((m) => String(m.id) === code);
    if (!match) { setError('Match not found'); return; }

    const flags = getMatchFlags(match);
    if (flags.canJoin) { await joinMatch(match, flags.needsAccept); return; }
    if (flags.canOpen || flags.canContinue || flags.canWatch) { navigate(`/draughts/duel/${match.id}`); return; }
    setError('You cannot join this match right now.');
  };

  const createTitle =
    createKind === CREATE_KIND_BOT ? 'Practice vs AI'
    : createKind === CREATE_KIND_REAL ? 'Real Challenge'
    : 'Friendly Challenge';

  const isRealChallenge = createKind === CREATE_KIND_REAL;

  const getStatusBadge = (status, isOngoing) => {
    if (isOngoing) return <span className="status-badge ongoing">In Progress</span>;
    if (status === 'pending') return <span className="status-badge pending">Waiting</span>;
    return <span className="status-badge completed">Completed</span>;
  };

  const getTimeLabel = (seconds) => (Number(seconds) === 0 ? 'No time' : `${seconds} second${seconds !== 1 ? 's' : ''}`);
  const getMatchTimerSeconds = (m) => {
    const value = Number(m?.turn_timeout_seconds);
    return Number.isInteger(value) && value >= 0 ? value : Number(turnTimeoutSeconds);
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon checkers-icon">🏁</div>
            <div>
              <h1 className="page-title">Draughts Arena</h1>
              <p className="page-subtitle">International Draughts (10×10) • Flying kings • Compulsory captures</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>

        {/* User Stats Card */}
        <div className="user-stats-card">
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-icon">💰</div>
              <div className="stat-info">
                <div className="stat-label">Balance</div>
                <div className="stat-value">{user?.balance ?? 0} wikipenny</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">⭐</div>
              <div className="stat-info">
                <div className="stat-label">Draughts Rating</div>
                <div className="stat-value">{user?.draughts_rating ?? 1200} ELO</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">🎮</div>
              <div className="stat-info">
                <div className="stat-label">Active Games</div>
                <div className="stat-value">
                  {myCreatedMatches.filter((m) => m.status === 'in_progress').length +
                   myJoinedMatches.filter((m) => m.status === 'in_progress').length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="quick-actions-card">
          <div className="quick-actions-row">
            <button
              className={`quick-action ${quickSearching ? 'searching' : ''}`}
              disabled={!userId || quickSearching}
              onClick={handleQuickSearch}
            >
              {quickSearching ? (
                <><span className="spinner-small"></span>Searching...</>
              ) : (
                <><span className="action-icon">⚡</span>Quick Match</>
              )}
            </button>

            <div className="join-by-code">
              <input
                className="code-input"
                value={joinId}
                onChange={(e) => setJoinId(e.target.value)}
                placeholder="Enter match ID"
              />
              <button className="join-btn" disabled={!joinId} onClick={onJoinByCode}>
                Join Match
              </button>
            </div>
          </div>

          {matchByJoinCode && (
            <div className="match-found">
              <span className="found-icon">🎯</span>
              Match found: {matchByJoinCode.creator_username || 'Host'} vs {matchByJoinCode.opponent_username || 'Open'}
            </div>
          )}
        </div>

        {/* Create Challenge Cards */}
        <div className="create-challenges">
          <div className="section-header">
            <h2 className="section-title">Create Challenge</h2>
            <p className="section-subtitle">Choose your game mode</p>
          </div>

          <div className="create-cards">
            <div className="create-card bot" onClick={() => openCreateModal(CREATE_KIND_BOT)}>
              <div className="create-icon">🤖</div>
              <div className="create-info">
                <div className="create-title">Practice vs AI</div>
                <div className="create-desc">Learn draughts against computer</div>
              </div>
              <div className="create-arrow">→</div>
            </div>

            <div className="create-card friendly" onClick={() => openCreateModal(CREATE_KIND_FRIENDLY)}>
              <div className="create-icon">👥</div>
              <div className="create-info">
                <div className="create-title">Friendly Challenge</div>
                <div className="create-desc">No wager required, play for fun</div>
              </div>
              <div className="create-arrow">→</div>
            </div>

            <div className="create-card real" onClick={() => openCreateModal(CREATE_KIND_REAL)}>
              <div className="create-icon">💰</div>
              <div className="create-info">
                <div className="create-title">Real Challenge</div>
                <div className="create-desc">Compete for wikipenny, climb ranks</div>
              </div>
              <div className="create-arrow">→</div>
            </div>
          </div>
        </div>

        {/* Matches Section */}
        <div className="matches-section">
          <div className="matches-tabs">
            <button
              className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
              onClick={() => setActiveTab('my')}
            >
              <span className="tab-icon">🎯</span>
              My Games
              <span className="tab-count">{myCreatedMatches.length + myJoinedMatches.length}</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'available' ? 'active' : ''}`}
              onClick={() => setActiveTab('available')}
            >
              <span className="tab-icon">🌍</span>
              Available
              <span className="tab-count">{availableMatches.length}</span>
            </button>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading matches...</span>
            </div>
          )}

          {!loading && activeTab === 'my' && (
            <>
              {myCreatedMatches.length > 0 && (
                <div className="matches-group">
                  <h3 className="group-title">Created by you</h3>
                  <div className="matches-list">
                    {myCreatedMatches.map((m) => {
                      const flags = getMatchFlags(m);
                      return (
                        <div key={`created-${m.id}`} className="match-card">
                          <div className="match-header">
                            <div className="match-id">Match #{m.id}</div>
                            <div className="match-badges">
                              <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                                {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                              </span>
                              {flags.isBetting && <span className="wager-badge">Wager: {m.wager}</span>}
                              {getStatusBadge(m.status, flags.isOngoing)}
                            </div>
                          </div>
                          <div className="match-players">
                            <div className="player">
                              <span className="player-role">You (Creator)</span>
                              <span className="player-name">{m.creator_username || 'You'}</span>
                            </div>
                            <div className="vs-divider">VS</div>
                            <div className="player">
                              <span className="player-role">Opponent</span>
                              <span className="player-name">{m.opponent_username || 'Waiting...'}</span>
                            </div>
                          </div>
                          <div className="match-details">
                            <span className="detail-item">⏱️ {getTimeLabel(getMatchTimerSeconds(m))}</span>
                            <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="match-invite-row">
                            <button
                              className="action-btn watch"
                              onClick={() => void shareMatchLink(m.id, flags.isBetting ? 'bet room' : 'friendly room')}
                              title="Copy invite link"
                              aria-label="Copy invite link"
                            >
                              📋
                            </button>
                          </div>
                          <div className="match-actions">
                            {flags.canCancel && (
                              <button className="action-btn cancel" onClick={() => void cancelMatch(m.id)}>Cancel</button>
                            )}
                            {(flags.canOpen || flags.canContinue) && (
                              <button className="action-btn play" onClick={() => navigate(`/draughts/duel/${m.id}`)}>
                                {flags.canContinue ? 'Continue →' : 'Open →'}
                              </button>
                            )}
                            {flags.canWatch && (
                              <button className="action-btn watch" onClick={() => navigate(`/draughts/duel/${m.id}`)}>Watch →</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {myJoinedMatches.length > 0 && (
                <div className="matches-group">
                  <h3 className="group-title">Joined by you</h3>
                  <div className="matches-list">
                    {myJoinedMatches.map((m) => {
                      const flags = getMatchFlags(m);
                      return (
                        <div key={`joined-${m.id}`} className="match-card">
                          <div className="match-header">
                            <div className="match-id">Match #{m.id}</div>
                            <div className="match-badges">
                              <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                                {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                              </span>
                              {flags.isBetting && <span className="wager-badge">Wager: {m.wager}</span>}
                              {getStatusBadge(m.status, flags.isOngoing)}
                            </div>
                          </div>
                          <div className="match-players">
                            <div className="player">
                              <span className="player-role">Host</span>
                              <span className="player-name">{m.creator_username || 'Unknown'}</span>
                            </div>
                            <div className="vs-divider">VS</div>
                            <div className="player">
                              <span className="player-role">You</span>
                              <span className="player-name">You</span>
                            </div>
                          </div>
                          <div className="match-details">
                            <span className="detail-item">⏱️ {getTimeLabel(getMatchTimerSeconds(m))}</span>
                            <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="match-actions">
                            {(flags.canOpen || flags.canContinue) && (
                              <button className="action-btn play" onClick={() => navigate(`/draughts/duel/${m.id}`)}>
                                {flags.canContinue ? 'Continue →' : 'Open →'}
                              </button>
                            )}
                            {flags.canWatch && (
                              <button className="action-btn watch" onClick={() => navigate(`/draughts/duel/${m.id}`)}>Watch →</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {myCreatedMatches.length === 0 && myJoinedMatches.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">🎮</div>
                  <div className="empty-title">No active games</div>
                  <div className="empty-desc">Create a challenge or join an available game to start playing</div>
                </div>
              )}
            </>
          )}

          {!loading && activeTab === 'available' && (
            <>
              <div className="search-bar">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  value={roomSearch}
                  onChange={(e) => setRoomSearch(e.target.value)}
                  placeholder="Search by match ID or player name..."
                />
                {roomSearch && (
                  <button className="clear-search" onClick={() => setRoomSearch('')}>×</button>
                )}
              </div>

              {filteredAvailableMatches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🌍</div>
                  <div className="empty-title">No available challenges</div>
                  <div className="empty-desc">
                    {roomSearch ? `No matches matching "${roomSearch}"` : 'Create a challenge to start playing'}
                  </div>
                  {roomSearch && (
                    <button className="clear-filters-btn" onClick={() => setRoomSearch('')}>Clear Search</button>
                  )}
                </div>
              ) : (
                <div className="matches-list">
                  {filteredAvailableMatches.map((m) => {
                    const flags = getMatchFlags(m);
                    return (
                      <div key={`available-${m.id}`} className="match-card available">
                        <div className="match-header">
                          <div className="match-id">Match #{m.id}</div>
                          <div className="match-badges">
                            <span className={`mode-badge ${flags.isBetting ? 'real' : 'friendly'}`}>
                              {flags.isBetting ? '💰 Real' : '🎮 Friendly'}
                            </span>
                            {flags.isBetting && <span className="wager-badge">Wager: {m.wager}</span>}
                          </div>
                        </div>
                        <div className="match-players">
                          <div className="player">
                            <span className="player-role">Host</span>
                            <span className="player-name">{m.creator_username || 'Unknown'}</span>
                          </div>
                          <div className="vs-divider">VS</div>
                          <div className="player">
                            <span className="player-role">Open</span>
                            <span className="player-name waiting">Waiting for opponent</span>
                          </div>
                        </div>
                        <div className="match-details">
                          <span className="detail-item">⏱️ {getTimeLabel(getMatchTimerSeconds(m))}</span>
                          <span className="detail-item">📅 {new Date(m.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="match-actions">
                          {flags.canJoin && (
                            <button className="action-btn join" onClick={() => void joinMatch(m, flags.needsAccept)}>
                              Join Challenge →
                            </button>
                          )}
                          {flags.canWatch && (
                            <button className="action-btn watch" onClick={() => navigate(`/draughts/duel/${m.id}`)}>Watch →</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal-container chess-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-icon">🏁</div>
                <h3 className="modal-title">{createTitle}</h3>
                <button className="modal-close" onClick={closeCreateModal}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Turn Timer</label>
                  <div className="time-options">
                    {options.map((o) => (
                      <button
                        key={o.seconds}
                        className={`time-option ${turnTimeoutSeconds === o.seconds ? 'selected' : ''}`}
                        onClick={() => setTurnTimeoutSeconds(o.seconds)}
                      >
                        <span className="time-icon">{o.icon}</span>
                        <span className="time-label">{o.label}</span>
                        <span className="time-desc">{o.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {isRealChallenge && (
                  <div className="form-group">
                    <label className="form-label">Wager Amount</label>
                    <div className="wager-input-wrapper">
                      <span className="wager-currency">💰</span>
                      <input
                        type="number"
                        className="form-input wager-input"
                        min={minimumWager}
                        max={Number(user?.balance || 0)}
                        value={wager}
                        onChange={(e) => setWager(Number(e.target.value))}
                      />
                      <span className="wager-suffix">wikipenny</span>
                    </div>
                    <div className="form-hint">
                      Minimum: {minimumWager} wikipenny &bull; Balance: {user?.balance ?? 0} wikipenny
                    </div>
                    {!canCreate && (
                      <div className="error-hint">
                        {Number(wager) < minimumWager
                            ? `⚠️ Minimum wager is ${minimumWager} wikipenny`
                            : '⚠️ Insufficient balance for this wager'}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  {createKind === CREATE_KIND_BOT ? (
                    <>
                      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="form-label">Play for Wager?</label>
                        <div className="wager-input-wrapper" style={{ marginBottom: '0.5rem' }}>
                          <span className="wager-currency">💰</span>
                          <input
                            type="number"
                            className="form-input wager-input"
                            min={0}
                            value={botWager}
                            onChange={(e) => setBotWager(Number(e.target.value))}
                            placeholder="0 for practice"
                          />
                          <span className="wager-suffix">WP</span>
                        </div>
                        <div className="form-hint">0 for free practice, or at least {minimumWager} WP for wagered match</div>
                      </div>
                      {Number(botWager) > 0 ? (
                        <div className="info-box" style={{ background: 'rgba(239, 68, 68, 0.1)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.2)', marginBottom: '1rem' }}>
                          <div style={{ color: '#ef4444', fontWeight: '600' }}>🔴 Level 7: Grandmaster (Locked)</div>
                          <div style={{ fontSize: '0.75rem', color: '#999' }}>Wagered bot matches are strictly grandmaster level. Time control clamped to 15s.</div>
                        </div>
                      ) : (
                        <>
                          <label className="form-label">Practice Level</label>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {[
                              { value: 1, label: 'Beginner' },
                              { value: 2, label: 'Normal' },
                              { value: 3, label: 'Expert' },
                            ].map((lvl) => (
                              <button
                                key={lvl.value}
                                type="button"
                                className={`time-option ${botLevel === lvl.value ? 'selected' : ''}`}
                                onClick={() => setBotLevel(lvl.value)}
                                style={{ flex: 1 }}
                              >
                                <span className="time-label">{lvl.label}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="form-label">Invite Friend (Optional)</label>
                      <select
                        className="form-select"
                        value={opponentId}
                        onChange={(e) => setOpponentId(e.target.value)}
                      >
                        <option value="">Open match (anyone can join)</option>
                        {friends.map((f) => (
                          <option key={f.friendship_id || f.friend_id} value={f.friend_id}>
                            {Boolean(friendsOnlineStatus[f.friend_id]) ? '🟢' : '⚪'} | {f.friend_username || f.username || 'Friend'}
                          </option>
                        ))}
                      </select>
                      <div className="form-hint">Send a direct challenge to a specific friend</div>
                    </>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-create"
                  disabled={isRealChallenge && !canCreate}
                  onClick={handleCreateFromModal}
                >
                  {createKind === CREATE_KIND_BOT ? (Number(botWager) > 0 ? 'Start Wagered Match →' : 'Start Practice →') : 'Create Challenge →'}
                </button>
                <button className="btn-cancel" onClick={closeCreateModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
